import { addYears, subYears } from 'date-fns';
import { useState, useMemo } from 'react';
import {
  getToday,
  parseDueDate,
  toISODate,
  getVisibleDays,
  isSameDayISO,
  isOverdue,
} from '@/utils/shared/dateUtils';
import { EnrichedTask } from '@/hooks/admin/AdminTodosTabs/useTasks';

export type Filters = {
  assignedUser: string;
  property: string;
  tag: string;
};

const DEFAULT_FILTERS: Filters = {
  assignedUser: '__all__',
  property: '__all__',
  tag: '__all__',
};

const useFilteredTasks = (tasks: EnrichedTask[]) => {
  const today = getToday();

  // 🔹 Generar todos los días desde hace 1 año hasta dentro de 1 año
  const startRange = subYears(today, 1);
  const endRange = addYears(today, 1);
  const totalDays = Math.ceil((endRange.getTime() - startRange.getTime()) / (1000 * 60 * 60 * 24)) + 1;

  const visibleDays = useMemo(
    () => getVisibleDays(startRange, totalDays),
    [startRange, totalDays]
  );

  // 🔹 Mantener el estado para filtros
  const [filters, setFilters] = useState<Filters>(DEFAULT_FILTERS);

  const clearFilters = () => setFilters(DEFAULT_FILTERS);

  const hasActiveFilters = useMemo(
    () => Object.values(filters).some((val) => val !== '__all__'),
    [filters],
  );

  const filteredTasks = useMemo(
    () =>
      tasks.filter(({ assigned_user_id, property_id, tag_id }) => {
        const matchUser =
          filters.assignedUser === '__all__' ||
          assigned_user_id === filters.assignedUser;

        const matchProperty =
          filters.property === '__all__' || property_id === filters.property;

        const matchTag = filters.tag === '__all__' || tag_id === filters.tag;

        return matchUser && matchProperty && matchTag;
      }),
    [tasks, filters],
  );

  const groupedTasks = useMemo(() => {
    const byDate: Record<string, (EnrichedTask & { isOverdue?: boolean })[]> = {};
    const backlog: (EnrichedTask & { isOverdue?: boolean })[] = [];

    filteredTasks.forEach((task) => {
      if (!task.due_date) {
        backlog.push(task);
        return;
      }

      const taskDate = parseDueDate(task.due_date);
      const isTaskCompleted = task.status === 'completed';
      const overdue = isOverdue(taskDate, isTaskCompleted);

      const targetDate = taskDate;
      const isVisible = visibleDays.some((d) => isSameDayISO(d, targetDate));
      const dateKey = toISODate(targetDate);

      if (isVisible) {
        if (!byDate[dateKey]) byDate[dateKey] = [];
        byDate[dateKey].push({ ...task, isOverdue: overdue });
      }
    });

    return { byDate, backlog };
  }, [filteredTasks, visibleDays]);

  return {
    filters,
    setFilters,
    clearFilters,
    hasActiveFilters,
    groupedTasks,
    visibleDays,
    today // lo devuelvo para que puedas scrollear hasta hoy
  };
};

export default useFilteredTasks;
